import React, { useState, useEffect } from "react";
import { useSiteConfig } from "components/Site/Contexts/SiteConfig";
import scrollIntoView from "scroll-into-view-if-needed";
import OutsideClickHandler from "react-outside-click-handler";

import { DropdownTrigger, DropdownContent } from "../../../AppBar/Dropdown";
import { BibleCtx, withBible } from "../../../Bible/BibleContainer";
import styled from "styled-components";
import { ChapterCtx, withChapter, StateChapter } from "../../ChapterContainer";
import { BookList } from "./styles";
import { BookChapterList } from "./BookChapterList";
import { AllBookChapterList } from "./AllBookChapterList";
import { getMaxMenuHeight } from "../utils";
import { isPhoneDevice } from "../../../../utils/mobile";
import { useScriptureRouting } from "components/Bible/useScriptureRouting";
import { Book } from "api/typedefs/Bible";

interface BookChapterDropdownProps {
  bible: BibleCtx;
  chapter: ChapterCtx;
  isOpen: boolean;
  toggle: () => void;
}

const Reference = styled.span`
  color: ${props => props.theme.appBarText};
  user-select: none;
`;

const getChapterReference = ({
  books,
  chapter,
  chapterId,
  limit
}: {
  books: Book[];
  chapter?: StateChapter;
  chapterId: string;
  limit?: number;
}) => {
  const [bookId, chapterNumber] = chapterId.split(".");
  const bookNumber = parseInt(bookId[0]); // !NaN if bookId starts with #
  const chapterBook = books.find(book => book.id === bookId);

  let chapterReference: string;
  if (chapter) {
    chapterReference = chapter.reference;
  } else if (books.length && chapterBook && chapterBook.name) {
    chapterReference = `${chapterBook.name} ${chapterNumber}`;
  } else if (bookNumber > 0) {
    chapterReference = `${bookNumber} ${bookId.slice(1)} ${chapterNumber}`;
  } else {
    chapterReference = `${bookId} ${chapterNumber}`;
  }

  if (limit && chapterReference.length > limit) {
    chapterReference =
      chapterBook && chapterBook.abbreviation
        ? `${chapterBook.abbreviation} ${chapterNumber}`
        : `${chapterReference.slice(0, limit - 3)}...`;
  }

  return chapterReference;
};

const BookChapterDropdown: React.FC<BookChapterDropdownProps> = ({
  bible,
  chapter,
  isOpen,
  toggle
}) => {
  const [selectedBookId, setSelectedBookId] = useState<string>("");
  const { includeAllBibles } = useSiteConfig();
  const { bibleId, chapterId, setChapter } = useScriptureRouting();

  useEffect(() => {
    const bookId = chapterId.split(".")[0];
    const target = document.getElementById(bookId);
    const boundary = document.getElementById("bible-chapter-dropdown-content");
    if (isOpen && bookId && target && boundary) {
      scrollIntoView(target, {
        scrollMode: "if-needed",
        boundary
      });
    }
  }, [isOpen, chapterId]);

  const currentBible = bible.biblesById[bibleId];

  const currentChapter = chapter.getChapter();

  const handleBookClick = (bookId: string) => {
    setSelectedBookId(bookId === selectedBookId ? "" : bookId);
  };

  const handleChapterClick = (chapterInfo: {
    bibleId: string;
    chapterId: string;
  }) => {
    setChapter({
      bibleId: chapterInfo.bibleId,
      chapterId: chapterInfo.chapterId
    });
    toggle();
  };

  const handleToggle = () => {
    setSelectedBookId(chapter.getChapter()?.bookId || "");
    toggle();
  };

  const books = currentBible?.books || [];
  const bookId = currentChapter ? currentChapter.bookId : null;

  const chapterReference = getChapterReference({
    books,
    chapter: currentChapter,
    chapterId,
    limit: isPhoneDevice() ? 7 : undefined
  });
  return (
    <div>
      <OutsideClickHandler onOutsideClick={() => isOpen && handleToggle()}>
        <DropdownTrigger arrow toggle={() => handleToggle()} isOpen={isOpen}>
          <Reference>{chapterReference}</Reference>
        </DropdownTrigger>
        {includeAllBibles ? (
          isOpen && (
            <AllBookChapterList
              books={books}
              chapter={chapter}
              handleChapterClick={handleChapterClick}
            />
          )
        ) : (
          <DropdownContent
            id="bible-chapter-dropdown-content"
            isOpen={isOpen}
            style={{ height: getMaxMenuHeight() }}
          >
            <BookList>
              {books.map(book => (
                <BookChapterList
                  key={book.id}
                  bookId={bookId}
                  selectedBookId={selectedBookId}
                  handleBookClick={handleBookClick}
                  handleChapterClick={handleChapterClick}
                  book={book}
                  chapter={chapter}
                />
              ))}
            </BookList>
          </DropdownContent>
        )}
      </OutsideClickHandler>
    </div>
  );
};

export default withChapter(withBible(BookChapterDropdown));
